import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Plus,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  UserPlus,
  MapPinned,
  Play,
  Printer,
  Undo2,
  RotateCcw,
  IndianRupee,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent } from '../../components/ui/Card';
import RefreshButton from '../../components/ui/RefreshButton';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { formatCurrency, formatDateTime, formatName, formatVehicleName, amountInWords } from '../../lib/utils';
import { bookingAPI, userAPI } from '../../services/api';
import CarLoader from '../../components/ui/CarLoader';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import JourneyInvoice from '../../components/Invoice/JourneyInvoice';
import { UserCog } from 'lucide-react';

const BookingList = () => {
  const navigate = useNavigate();
  const { travelsInfo, user: authUser } = useAuth();
  const { settings } = useSettings();
  const canTakeAction = ['owner', 'manager', 'driver'].includes(authUser?.role);
  const isAdmin = authUser?.role === 'admin'; // read-only monitor: server-paginated 12/page, no actions
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [bookingToReject, setBookingToReject] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '', type: '' });
  const [invoiceBooking, setInvoiceBooking] = useState(null);
  const [tripConfirmModal, setTripConfirmModal] = useState(false);
  const [tripConfirmAction, setTripConfirmAction] = useState(null); // { type: 'start'|'complete', bookingId, vehicleName, amount }
  // Payment fields for complete-trip modal
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  // Trip-end pricing: which rate to apply (per-day vs per-km) + km driven
  const [completeRentalType, setCompleteRentalType] = useState('daily');
  const [completeKm, setCompleteKm] = useState('');

  useEffect(() => {
    // Everyone loads the full list once; filtering + pagination are client-side
    // so filters apply across ALL records (admin sees all owners' bookings).
    fetchBookings();
    if (!isAdmin) fetchDrivers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open booking details if id is in URL params
  useEffect(() => {
    const bookingId = searchParams.get('id');
    if (bookingId && bookings.length > 0 && !selectedBooking) {
      const booking = bookings.find(b => b._id === bookingId);
      if (booking) {
        viewDetails(booking);
      }
    }
  }, [bookings, searchParams]);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter, fromDate, toDate]);

  // Reset to page 1 only when a FILTER changes — NOT when `bookings` refetches.
  // (Admin is server-paginated: resetting on every refetch fought the page change
  //  and snapped back to page 1.)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, fromDate, toDate]);

  // Set the From/To inputs to the default range (Settings -> defaultDaysFilter,
  // default 15 days). Used on load AND by the "Reset to default" button.
  const applyDefaultDateRange = () => {
    const days = settings?.defaultDaysFilter ?? 15;
    if (days === 0) {
      setFromDate(''); setToDate('');
      return;
    }
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - (days - 1));
    setFromDate(start.toISOString().split('T')[0]);
    setToDate(today.toISOString().split('T')[0]);
  };

  // Apply the default range on load for admin and owners alike.
  useEffect(() => {
    applyDefaultDateRange();
  }, [settings?.defaultDaysFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDrivers = async () => {
    try {
      const response = await userAPI.getStaffMembers({ role: 'driver' });
      setDrivers(response.data.staffMembers || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      // Everyone gets the full list (admin = all owners); client filters + paginates.
      const response = await bookingAPI.getAllBookings();
      const bookingsData = response.data.bookings || response.data || [];
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerId?.phone?.includes(searchTerm) ||
        booking.customerPhone?.includes(searchTerm) ||
        booking.carId?.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.carId?.model?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Date range filter — by when the booking was MADE (createdAt), so a
    // booking made today for a future trip still shows. Pending bookings are
    // always shown regardless of the date filter so urgent ones aren't missed.
    if (fromDate) {
      const from = new Date(fromDate); from.setHours(0, 0, 0, 0);
      filtered = filtered.filter(b => b.status === 'pending' || (b.createdAt && new Date(b.createdAt) >= from));
    }
    if (toDate) {
      const to = new Date(toDate); to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(b => b.status === 'pending' || (b.createdAt && new Date(b.createdAt) <= to));
    }

    setFilteredBookings(filtered);
  };

  // Manual refresh — used by the refresh button
  const handleRefresh = async () => {
    setRefreshing(true);
    try { await fetchBookings(); } finally { setRefreshing(false); }
  };

  // Computed slice for the current page
  // Client-side pagination for everyone: filters apply to the full list, then
  // we slice 12 per page. (Admin loads all owners' bookings.)
  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / perPage));
  const pagedBookings = filteredBookings.slice((currentPage - 1) * perPage, currentPage * perPage);

  // If filtering shrinks the list below the current page, snap back into range.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Show approve confirmation modal
  const handleApproveBooking = (bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this booking';
    const customerName = formatName(booking?.customerName || booking?.customerId?.name) || 'Customer';
    setTripConfirmAction({ type: 'approve', bookingId, vehicleName, customerName, amount: booking?.totalCost || 0 });
    setTripConfirmModal(true);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await bookingAPI.updateBookingStatus(bookingId, newStatus);
      fetchBookings();

      // Show success modal with appropriate message
      const successMessages = {
        confirmed: {
          title: 'Booking Approved!',
          message: 'The booking has been confirmed. Customer will be notified.',
          type: 'success'
        },
        active: {
          title: 'Trip Started!',
          message: 'The booking is now active. Have a safe trip!',
          type: 'info'
        },
        completed: {
          title: 'Trip Completed!',
          message: 'The booking has been marked as completed.',
          type: 'success'
        }
      };

      const msgConfig = successMessages[newStatus] || {
        title: 'Status Updated!',
        message: `Booking status changed to ${newStatus}`,
        type: 'info'
      };

      setSuccessMessage(msgConfig);
      setSuccessModal(true);
    } catch (error) {
      console.error('Error updating booking status:', error);
      setSuccessMessage({
        title: 'Error',
        message: 'Failed to update booking status. Please try again.',
        type: 'error'
      });
      setSuccessModal(true);
    }
  };

  // Revert booking status to previous state
  const handleRevertStatus = (bookingId, currentStatus) => {
    const booking = bookings.find(b => b._id === bookingId);
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this booking';
    const revertMap = { completed: 'active', active: 'confirmed', confirmed: 'pending' };
    const revertTo = revertMap[currentStatus];
    if (!revertTo) return;

    setTripConfirmAction({
      type: 'revert-status',
      bookingId,
      vehicleName,
      currentStatus,
      revertTo
    });
    setTripConfirmModal(true);
  };

  // Open confirmation modal for trip actions
  const handleStartTrip = (bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    // Owner with no driver assigned drives the trip themselves →
    // use the full driver Start Trip flow (odometer, GPS, live journey)
    if (authUser?.role === 'owner' && !booking?.driverId) {
      navigate(`/driver/active-trip/booking/${bookingId}`);
      return;
    }
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this vehicle';
    setTripConfirmAction({ type: 'start', bookingId, vehicleName });
    setTripConfirmModal(true);
  };

  const handleCompleteTrip = (bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this vehicle';
    // Reset payment fields
    setPaymentMode('cash');
    setPaymentReference('');
    setPaymentNotes('');
    setPaymentProofFile(null);
    setPaymentProofPreview(null);
    const remaining = Math.max(0, (booking?.totalCost || 0) - (booking?.totalPaid || 0));
    setPaymentAmount(remaining.toString());
    // Default the pricing to what the customer booked; prefill km from estimate/odometer.
    setCompleteRentalType(booking?.rentalType || 'daily');
    setCompleteKm(String(booking?.actualKm || booking?.estimatedKm || ''));
    setTripConfirmAction({
      type: 'complete', bookingId, vehicleName,
      amount: booking?.totalCost || 0, totalPaid: booking?.totalPaid || 0, remaining,
      car: booking?.carId || {}, startDate: booking?.startDate, endDate: booking?.endDate, hours: booking?.hours || 0
    });
    setTripConfirmModal(true);
  };

  // Live trip-end cost from the selected rate (per-day vs per-km) + km/days.
  const computeCompleteCost = () => {
    const a = tripConfirmAction;
    if (!a || !a.car) return a?.amount || 0;
    const car = a.car;
    const start = new Date(a.startDate);
    const end = new Date(a.endDate);
    const days = Math.max(1, Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)));
    const km = Number(completeKm) || 0;
    switch (completeRentalType) {
      case 'per-km':
      case 'km-based':
        return (car.pricePerKm || 0) * km;
      case 'outstation':
        return (car.outstationPricePerKm || car.pricePerKm || 0) * km + (car.driverBataPerDay || 0) * days;
      case 'hourly':
      case 'local':
        return (car.pricePerHour || 0) * (a.hours || 0);
      default:
        return (car.pricePerDay || 0) * days;
    }
  };

  // Descriptive base-cost label for the breakdown (e.g. "359 km × ₹20" or "2 days × ₹1000")
  const baseLabel = (b) => {
    const car = b.carId || {};
    if (['per-km', 'km-based', 'outstation'].includes(b.rentalType) && b.actualKm) {
      const rate = b.rentalType === 'outstation' ? (car.outstationPricePerKm || car.pricePerKm) : car.pricePerKm;
      return `Base (${b.actualKm} km × ₹${rate || 0})`;
    }
    if (['daily', 'rental'].includes(b.rentalType)) {
      const days = Math.max(1, Math.ceil(Math.abs(new Date(b.endDate) - new Date(b.startDate)) / 86400000));
      return `Base (${days} day${days > 1 ? 's' : ''} × ₹${car.pricePerDay || 0})`;
    }
    return 'Base';
  };

  // Map a booking into the journey shape so it renders with the SAME JourneyInvoice.
  const bookingToJourney = (b) => {
    if (!b) return null;
    const cust = (b.customerId && typeof b.customerId === 'object')
      ? b.customerId
      : { name: b.customerName, phone: b.customerPhone, email: b.customerEmail };
    return {
      _id: b._id,
      carId: b.carId,
      customerId: cust,
      driverId: (b.driverId && typeof b.driverId === 'object') ? b.driverId : null,
      bookingId: b._id,
      rentalType: b.rentalType,
      actualDistance: b.actualKm || 0,
      actualDuration: b.hours || 0,
      startOdometer: b.startOdometer || 0,
      endOdometer: b.endOdometer || 0,
      startTime: b.startDate,
      endTime: b.endDate,
      fromLocation: { address: b.pickupLocation || '' },
      toLocation: { address: b.dropoffLocation || '' },
      stops: Array.isArray(b.stops) ? b.stops : [],
      baseCost: b.baseCost || 0,
      tollCharges: b.tollCharges || 0,
      parkingCharges: b.parkingCharges || 0,
      otherCharges: b.otherCharges || 0,
      otherChargesNote: b.otherChargesNote || '',
      discount: b.discount || 0,
      discountNote: b.discountNote || '',
      totalCost: b.totalCost,
      paymentStatus: b.paymentStatus,
      paidAmount: b.totalPaid || 0
    };
  };

  // Sale (Buy) — mark vehicle as sold, with confirmation
  const handleMarkSold = (bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this vehicle';
    const totalCost = booking?.totalCost || 0;
    const totalPaid = booking?.totalPaid || 0;
    setTripConfirmAction({
      type: 'mark-sold',
      bookingId,
      vehicleName,
      amount: totalCost || booking?.offerPrice || 0,
      totalPaid,
      remaining: Math.max(0, totalCost - totalPaid),
      notFullyPaid: totalPaid < totalCost && totalCost > 0
    });
    setTripConfirmModal(true);
  };

  // Record a payment (advance / partial / full) against any booking — opens
  // the existing payment modal. Used for both rental and buy bookings.
  // NOTE: amount input is intentionally LEFT EMPTY so the owner types the
  // actual amount paid; pre-filling the remaining caused advances to be
  // accidentally recorded as full payments.
  const handleRecordPayment = (booking) => {
    setPaymentMode('cash');
    setPaymentReference('');
    setPaymentNotes('');
    setPaymentProofFile(null);
    setPaymentProofPreview(null);
    setPaymentAmount('');
    const remaining = Math.max(0, (booking?.totalCost || 0) - (booking?.totalPaid || 0));
    setTripConfirmAction({
      type: 'mark-paid',
      bookingId: booking._id,
      vehicleName: booking.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this booking',
      amount: booking.totalCost || 0,
      totalPaid: booking.totalPaid || 0,
      remaining,
      currentStatus: booking.status
    });
    setTripConfirmModal(true);
  };

  // Sale (Buy) — revert a sold vehicle back to confirmed, with confirmation
  const handleRevertSale = (bookingId) => {
    const booking = bookings.find(b => b._id === bookingId);
    const vehicleName = booking?.carId ? formatVehicleName(booking.carId, { showYear: false }) : 'this vehicle';
    setTripConfirmAction({ type: 'revert-sale', bookingId, vehicleName });
    setTripConfirmModal(true);
  };

  // Execute trip/payment action after modal confirmation
  const confirmTripAction = async () => {
    if (!tripConfirmAction) return;
    const { type, bookingId, currentStatus } = tripConfirmAction;
    setTripConfirmModal(false);
    try {
      if (type === 'approve') {
        await bookingAPI.updateBookingStatus(bookingId, 'confirmed');
        setSuccessMessage({ title: 'Booking Approved!', message: 'The booking has been confirmed. Customer will be notified.', type: 'success' });
      } else if (type === 'start') {
        await bookingAPI.startTrip(bookingId);
        setSuccessMessage({ title: 'Trip Started!', message: 'Trip is now active. Have a safe journey!', type: 'success' });
      } else if (type === 'revert-status') {
        await bookingAPI.updateBookingStatus(bookingId, tripConfirmAction.revertTo);
        setSuccessMessage({ title: 'Status Reverted', message: `Booking reverted to ${tripConfirmAction.revertTo}.`, type: 'warning' });
      } else if (type === 'mark-sold') {
        await bookingAPI.updateBookingStatus(bookingId, 'completed');
        setSuccessMessage({ title: 'Marked as Sold!', message: 'The vehicle has been marked as sold. Customer will be notified.', type: 'success' });
      } else if (type === 'revert-sale') {
        await bookingAPI.updateBookingStatus(bookingId, 'confirmed');
        setSuccessMessage({ title: 'Reverted', message: 'Booking reverted to confirmed (not sold).', type: 'warning' });
      } else if (type === 'revert-payment') {
        await bookingAPI.updateBookingStatus(bookingId, currentStatus, undefined, 'pending');
        setSuccessMessage({ title: 'Payment Reverted', message: 'Payment status changed to pending.', type: 'warning' });
      } else if (type === 'mark-paid') {
        // Record partial/full payment — require an explicit amount from the owner.
        if (!paymentAmount || Number(paymentAmount) <= 0) {
          setSuccessMessage({ title: 'Enter Amount', message: 'Please enter the payment amount.', type: 'error' });
          setSuccessModal(true);
          return;
        }
        const formData = new FormData();
        formData.append('amount', paymentAmount);
        formData.append('paymentMode', paymentMode);
        if (paymentReference) formData.append('paymentReference', paymentReference);
        if (paymentNotes) formData.append('paymentNotes', paymentNotes);
        if (paymentProofFile) formData.append('paymentProof', paymentProofFile);
        await bookingAPI.recordPayment(bookingId, formData);
        setSuccessMessage({ title: 'Payment Recorded!', message: 'Payment marked as paid with details saved.', type: 'success' });
      } else {
        // Build FormData with payment info + optional photo
        const formData = new FormData();
        formData.append('amount', paymentAmount || tripConfirmAction.remaining || 0);
        formData.append('paymentStatus', 'paid');
        formData.append('paymentMode', paymentMode);
        // Final pricing: the rate applied (per-day/per-km) + km driven, so the
        // backend recomputes the total from actual usage.
        formData.append('rentalType', completeRentalType);
        if (completeKm) formData.append('actualKm', completeKm);
        if (paymentReference) formData.append('paymentReference', paymentReference);
        if (paymentNotes) formData.append('paymentNotes', paymentNotes);
        if (paymentProofFile) formData.append('paymentProof', paymentProofFile);
        await bookingAPI.completeTrip(bookingId, formData);
        setSuccessMessage({ title: 'Trip Completed!', message: 'Payment recorded. Vehicle is now available.', type: 'success' });
      }
      fetchBookings();
      setSuccessModal(true);
    } catch (error) {
      setSuccessMessage({ title: 'Error', message: error.response?.data?.message || `Failed to ${type}`, type: 'error' });
      setSuccessModal(true);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending', icon: Clock },
      confirmed: { variant: 'info', label: 'Confirmed', icon: CheckCircle },
      active: { variant: 'success', label: 'Active', icon: CheckCircle },
      completed: { variant: 'secondary', label: 'Completed', icon: CheckCircle },
      cancelled: { variant: 'danger', label: 'Cancelled', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const viewDetails = (booking) => {
    setSelectedBooking(booking);
    setSelectedDriver(booking.driverId?._id || booking.driverId || '');
    setShowDetailsModal(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedBooking || !selectedDriver) {
      setSuccessMessage({
        title: 'Select Driver',
        message: 'Please select a driver to assign',
        type: 'warning'
      });
      setSuccessModal(true);
      return;
    }

    try {
      setAssigningDriver(true);
      await bookingAPI.assignDriver(selectedBooking._id, selectedDriver);
      fetchBookings();
      setShowDetailsModal(false);
      setSuccessMessage({
        title: 'Driver Assigned!',
        message: 'Driver has been assigned successfully. They will be notified.',
        type: 'success'
      });
      setSuccessModal(true);
    } catch (error) {
      console.error('Error assigning driver:', error);
      setSuccessMessage({
        title: 'Error',
        message: 'Failed to assign driver. Please try again.',
        type: 'error'
      });
      setSuccessModal(true);
    } finally {
      setAssigningDriver(false);
    }
  };

  const handleUnassignDriver = async () => {
    if (!selectedBooking) return;

    try {
      setAssigningDriver(true);
      await bookingAPI.unassignDriver(selectedBooking._id);
      fetchBookings();
      setShowDetailsModal(false);
      setSuccessMessage({
        title: 'Driver Unassigned',
        message: 'Driver has been removed from this booking.',
        type: 'info'
      });
      setSuccessModal(true);
    } catch (error) {
      console.error('Error unassigning driver:', error);
      setSuccessMessage({
        title: 'Error',
        message: 'Failed to unassign driver. Please try again.',
        type: 'error'
      });
      setSuccessModal(true);
    } finally {
      setAssigningDriver(false);
    }
  };

  // Open reject modal
  const openRejectModal = (booking) => {
    setBookingToReject(booking);
    setRejectReason('');
    setRejectModal(true);
    setShowDetailsModal(false);
  };

  // Handle rejection with reason
  const handleRejectBooking = async () => {
    if (!bookingToReject) return;

    try {
      await bookingAPI.updateBookingStatus(bookingToReject._id, 'cancelled', rejectReason);
      fetchBookings();
      setRejectModal(false);
      setBookingToReject(null);
      setRejectReason('');

      // Show success message
      setSuccessMessage({
        title: 'Booking Rejected',
        message: 'The booking has been cancelled. Customer will see the rejection reason.',
        type: 'warning'
      });
      setSuccessModal(true);
    } catch (error) {
      console.error('Error rejecting booking:', error);
      setSuccessMessage({
        title: 'Error',
        message: 'Failed to reject booking. Please try again.',
        type: 'error'
      });
      setSuccessModal(true);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bookings"
        description="Manage all your vehicle bookings"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={handleRefresh} loading={refreshing} />
            <Button variant="primary" onClick={() => navigate('/bookings/create')}>
              <Plus className="h-5 w-5 mr-2" />
              New Booking
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Search by booking ID, customer, or car..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-sm h-8"
            />
            {/* Date range filter (default = owner's "Show last N days" setting) */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500 font-medium">From</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="text-xs h-8 px-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500" />
              <label className="text-xs text-gray-500 font-medium">To</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                className="text-xs h-8 px-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500" />
              <button onClick={applyDefaultDateRange} className="text-xs text-blue-600 hover:underline">Reset to default</button>
              <span className="text-[11px] text-gray-400 ml-auto hidden sm:inline">Default range from Settings</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'All', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', activeColor: 'bg-gray-700 text-white' },
                { value: 'pending', label: 'Pending', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', activeColor: 'bg-yellow-500 text-white' },
                { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', activeColor: 'bg-blue-500 text-white' },
                { value: 'active', label: 'Active', color: 'bg-green-50 text-green-700 hover:bg-green-100', activeColor: 'bg-green-500 text-white' },
                { value: 'completed', label: 'Completed', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100', activeColor: 'bg-purple-500 text-white' },
                { value: 'cancelled', label: 'Cancelled', color: 'bg-red-50 text-red-700 hover:bg-red-100', activeColor: 'bg-red-500 text-white' },
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                    statusFilter === status.value
                      ? status.activeColor + ' shadow-sm'
                      : status.color
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      {loading ? (
        <Card>
          <CardContent className="py-6">
            <CarLoader message="Loading bookings..." />
          </CardContent>
        </Card>
      ) : filteredBookings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-6">
            <Calendar className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No bookings found</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Booking ID</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Vehicle</TableHead>
                  <TableHead className="text-xs">Driver</TableHead>
                  <TableHead className="text-xs">Start Date</TableHead>
                  <TableHead className="text-xs">Amount</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Payment</TableHead>
                  <TableHead className="text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedBookings.map((booking) => (
                  <TableRow key={booking._id}>
                    <TableCell className="text-xs font-medium">
                      #{booking._id.slice(-6)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-sm font-medium">
                          {formatName(booking.customerId?.name || booking.customerName) || 'N/A'}
                        </div>
                        <div className="text-xs text-blue-600 font-medium">
                          {booking.customerId?.phone || booking.customerPhone || '-'}
                        </div>
                        {(booking.customerId?.email || booking.customerEmail) && (
                          <div className="text-xs text-gray-500">
                            {booking.customerId?.email || booking.customerEmail}
                          </div>
                        )}
                        {booking.rentalType === 'sale' && (
                          <>
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 bg-red-50 text-red-600 rounded">
                              🛒 Buy enquiry{booking.offerPrice ? ` · Offer ${formatCurrency(booking.offerPrice)}` : ''}
                            </div>
                            {booking.status !== 'pending' && (booking.totalCost || 0) > 0 && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                                Paid {formatCurrency(booking.totalPaid || 0)} / {formatCurrency(booking.totalCost)}
                                {(booking.totalPaid || 0) > 0 && (booking.totalPaid || 0) < booking.totalCost && ' · Advance'}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{formatVehicleName(booking.carId, { showYear: false })}</div>
                      <div className="text-xs text-gray-500">
                        {booking.carId?.registrationNumber}
                      </div>
                    </TableCell>
                    <TableCell>
                      {booking.rentalType === 'sale' ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : booking.driverId ? (
                        <div>
                          <div className="text-xs font-medium text-green-700">
                            {formatName(booking.driverId?.name)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.driverId?.phone}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => viewDetails(booking)}
                          className="flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          Assign
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{formatDateTime(booking.startDate)}</TableCell>
                    <TableCell className="text-xs font-semibold">
                      {formatCurrency(booking.totalCost || 0)}
                    </TableCell>
                    <TableCell>{getStatusBadge(booking.status)}</TableCell>
                    <TableCell>
                      {(() => {
                        const paid = booking.totalPaid || 0;
                        const total = booking.totalCost || 0;
                        const remaining = Math.max(0, total - paid);
                        if (paid >= total && paid > 0) {
                          return <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">✓ Paid</span>;
                        } else if (paid > 0) {
                          return (
                            <div>
                              <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Partial</span>
                              <p className="text-[10px] text-red-500 mt-0.5 whitespace-nowrap">₹{remaining.toLocaleString()} due</p>
                            </div>
                          );
                        } else if (booking.status === 'completed') {
                          return <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">₹{total.toLocaleString()} due</span>;
                        } else if (booking.status === 'active') {
                          return <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending</span>;
                        } else {
                          return <span className="text-xs text-gray-400">-</span>;
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {booking.status === 'pending' && canTakeAction ? (
                          <>
                            <button
                              onClick={() => handleApproveBooking(booking._id)}
                              className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => openRejectModal(booking)}
                              className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Reject
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => viewDetails(booking)}
                              className="inline-flex items-center px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-all"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </button>
                            {canTakeAction && booking.status === 'confirmed' && booking.rentalType !== 'sale' && (
                              <>
                                <button
                                  onClick={() => handleStartTrip(booking._id)}
                                  className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                                >
                                  <Play className="h-3 w-3 mr-1" />
                                  Start Trip
                                </button>
                                <button
                                  onClick={() => handleRevertStatus(booking._id, 'confirmed')}
                                  className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                  Revert
                                </button>
                              </>
                            )}
                            {canTakeAction && booking.status === 'confirmed' && booking.rentalType === 'sale' && (
                              <>
                                {booking.paymentStatus !== 'paid' && (
                                  <button
                                    onClick={() => handleRecordPayment(booking)}
                                    className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                                  >
                                    <IndianRupee className="h-3 w-3 mr-1" />
                                    + Payment
                                  </button>
                                )}
                                <button
                                  onClick={() => handleMarkSold(booking._id)}
                                  className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Mark Sold
                                </button>
                              </>
                            )}
                            {canTakeAction && (booking.status === 'active' || booking.status === 'in-service') && (
                              <>
                                <button
                                  onClick={() => handleCompleteTrip(booking._id)}
                                  className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Complete
                                </button>
                              </>
                            )}
                            {/* Generate Invoice — directly from list for completed bookings */}
                            {booking.status === 'completed' && (
                              <button
                                onClick={() => setInvoiceBooking(booking)}
                                className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                              >
                                <Printer className="h-3 w-3 mr-1" />
                                Invoice
                              </button>
                            )}
                            {/* Sold sale — allow reverting back to confirmed */}
                            {canTakeAction && booking.status === 'completed' && booking.rentalType === 'sale' && (
                              <button
                                onClick={() => handleRevertSale(booking._id)}
                                className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs font-medium rounded-lg shadow-sm hover:shadow transition-all"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Revert
                              </button>
                            )}
                          </>
                        )}
                        {canTakeAction && booking.rentalType !== 'sale' && !booking.driverId && (booking.status === 'pending' || booking.status === 'confirmed') && (
                          <button
                            onClick={() => viewDetails(booking)}
                            className="inline-flex items-center px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-medium rounded-lg transition-all"
                          >
                            <UserPlus className="h-3 w-3 mr-1" />
                            Assign
                          </button>
                        )}
                        {booking.status === 'active' && booking.trackingCode && (
                          <button
                            onClick={() => navigate(`/track/${booking.trackingCode}`)}
                            className="inline-flex items-center px-2.5 py-1 bg-green-50 hover:bg-green-100 text-green-600 text-xs font-medium rounded-lg transition-all"
                          >
                            <MapPinned className="h-3 w-3 mr-1" />
                            Track
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {filteredBookings.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-2 py-3 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-500">
                  {`Showing ${Math.min((currentPage - 1) * perPage + 1, filteredBookings.length)}–${Math.min(currentPage * perPage, filteredBookings.length)} of ${filteredBookings.length}`}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="px-2 text-xs font-medium text-gray-700">Page {currentPage} of {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Booking Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Booking Details"
        size="md"
      >
        {selectedBooking && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Booking ID</p>
                <p className="font-medium">#{selectedBooking._id.slice(-6)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="mt-1">{getStatusBadge(selectedBooking.status)}</div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Customer</p>
                <p className="font-medium">
                  {formatName(selectedBooking.customerId?.name || selectedBooking.customerName) || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-blue-600">
                  {selectedBooking.customerId?.phone || selectedBooking.customerPhone || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">
                  {selectedBooking.customerId?.email || selectedBooking.customerEmail || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vehicle</p>
                <p className="font-medium">
                  {formatVehicleName(selectedBooking.carId, { showYear: false })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Registration</p>
                <p className="font-medium">{selectedBooking.carId?.registrationNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Start Date</p>
                <p className="font-medium">{formatDateTime(selectedBooking.startDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">End Date</p>
                <p className="font-medium">{formatDateTime(selectedBooking.endDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Cost</p>
                <p className="font-medium text-blue-600 text-lg">
                  {formatCurrency(selectedBooking.totalCost || 0)}
                </p>
              </div>
            </div>

            {/* Cost Breakdown — same view as the journey */}
            {selectedBooking.rentalType !== 'sale' && (selectedBooking.baseCost > 0 || selectedBooking.tollCharges > 0 || selectedBooking.parkingCharges > 0 || selectedBooking.discount > 0) && (
              <div className="pt-4 border-t">
                <p className="font-semibold text-gray-900 mb-3">₹ Cost Breakdown</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{baseLabel(selectedBooking)}</span>
                    <span className="font-medium">{formatCurrency(selectedBooking.baseCost || selectedBooking.totalCost || 0)}</span>
                  </div>
                  {selectedBooking.tollCharges > 0 && (
                    <div className="flex justify-between"><span className="text-gray-600">Toll</span><span className="font-medium">+ {formatCurrency(selectedBooking.tollCharges)}</span></div>
                  )}
                  {selectedBooking.parkingCharges > 0 && (
                    <div className="flex justify-between"><span className="text-gray-600">Parking</span><span className="font-medium">+ {formatCurrency(selectedBooking.parkingCharges)}</span></div>
                  )}
                  {selectedBooking.otherCharges > 0 && (
                    <div className="flex justify-between"><span className="text-gray-600">Other{selectedBooking.otherChargesNote ? ` (${selectedBooking.otherChargesNote})` : ''}</span><span className="font-medium">+ {formatCurrency(selectedBooking.otherCharges)}</span></div>
                  )}
                  {selectedBooking.discount > 0 && (
                    <div className="flex justify-between"><span className="text-green-600">Discount{selectedBooking.discountNote ? ` (${selectedBooking.discountNote})` : ''}</span><span className="font-medium text-green-600">- {formatCurrency(selectedBooking.discount)}</span></div>
                  )}
                  <div className="flex justify-between pt-2 border-t font-bold">
                    <span className="text-gray-900">Grand Total</span>
                    <span className="text-blue-600">{formatCurrency(selectedBooking.totalCost || 0)}</span>
                  </div>
                  <p className="text-[11px] italic text-gray-500 text-right">{amountInWords(selectedBooking.totalCost || 0)}</p>
                </div>
              </div>
            )}

            {/* Driver Assignment Section (rentals only — not for vehicle sales) */}
            {selectedBooking.rentalType !== 'sale' && (
            <div className="pt-4 border-t">
              <div className="flex items-center mb-3">
                <UserCog className="h-5 w-5 mr-2 text-gray-600" />
                <p className="font-semibold text-gray-900">Driver Assignment</p>
              </div>

              {selectedBooking.driverId ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Assigned Driver:</span>{' '}
                    {formatName(selectedBooking.driverId?.name || 'Driver')}
                  </p>
                  {selectedBooking.driverId?.phone && (
                    <p className="text-sm text-green-700">
                      Phone: {selectedBooking.driverId.phone}
                    </p>
                  )}
                  {/* Only show Unassign button for pending/confirmed bookings, not for active/completed */}
                  {canTakeAction && selectedBooking.status !== 'completed' && selectedBooking.status !== 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
                      onClick={handleUnassignDriver}
                      disabled={assigningDriver}
                    >
                      {assigningDriver ? 'Processing...' : 'Unassign Driver'}
                    </Button>
                  )}
                  {(selectedBooking.status === 'completed' || selectedBooking.status === 'active') && (
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Driver cannot be changed for {selectedBooking.status} bookings
                    </p>
                  )}
                </div>
              ) : canTakeAction ? (
                <div className="space-y-3">
                  <Select
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                  >
                    <option value="">Select a driver...</option>
                    {drivers.filter(d => d.isActive !== false).map(driver => (
                      <option key={driver._id} value={driver._id}>
                        {formatName(driver.name)} - {driver.phone}
                      </option>
                    ))}
                  </Select>
                  <Button
                    onClick={handleAssignDriver}
                    disabled={!selectedDriver || assigningDriver}
                    className="w-full"
                  >
                    {assigningDriver ? 'Assigning...' : 'Assign Driver'}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No driver assigned</p>
              )}
            </div>
            )}

            {/* Action Buttons for Pending Bookings */}
            {canTakeAction && selectedBooking.status === 'pending' && (
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 mb-3 text-center">This booking is waiting for your approval</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleApproveBooking(selectedBooking._id);
                    }}
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Approve Booking
                  </button>
                  <button
                    onClick={() => openRejectModal(selectedBooking)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    Reject Booking
                  </button>
                </div>
              </div>
            )}

            {/* Status Timeline */}
            {selectedBooking.statusHistory && selectedBooking.statusHistory.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Status Timeline</p>
                <div className="space-y-1">
                  {selectedBooking.statusHistory.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        h.status === 'pending' ? 'bg-yellow-400' :
                        h.status === 'confirmed' ? 'bg-blue-400' :
                        h.status === 'active' ? 'bg-purple-400' :
                        h.status === 'completed' ? 'bg-green-400' :
                        h.status === 'cancelled' ? 'bg-red-400' : 'bg-gray-400'
                      }`} />
                      <span className="font-medium capitalize">{h.status}</span>
                      <span className="text-gray-400">—</span>
                      <span className="text-gray-500">{new Date(h.changedAt).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</span>
                      {h.note && <span className="text-gray-400 italic">({h.note})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Summary + History + Invoice */}
            {(selectedBooking.status === 'confirmed' || selectedBooking.status === 'active' || selectedBooking.status === 'completed') && (
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Payment</p>

                {/* Balance Summary */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total</span>
                    <span className="font-bold">₹{(selectedBooking.totalCost || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-green-600">Paid</span>
                    <span className="font-bold text-green-600">₹{(selectedBooking.totalPaid || 0).toLocaleString()}</span>
                  </div>
                  {(selectedBooking.totalCost || 0) - (selectedBooking.totalPaid || 0) > 0 && (
                    <div className="flex justify-between text-sm mt-1 border-t pt-1">
                      <span className="text-red-600 font-semibold">Remaining</span>
                      <span className="font-bold text-red-600">₹{((selectedBooking.totalCost || 0) - (selectedBooking.totalPaid || 0)).toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Payment History List */}
                {selectedBooking.payments && selectedBooking.payments.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selectedBooking.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2 text-xs">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span className="font-medium capitalize">{(p.mode || 'cash').replace('_', ' ')}</span>
                          {p.reference && <span className="text-gray-400">Ref: {p.reference}</span>}
                          {p.proofUrl && <a href={p.proofUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">📷</a>}
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-green-700">₹{(p.amount || 0).toLocaleString()}</span>
                          <span className="text-gray-400 ml-2">{new Date(p.recordedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  {canTakeAction && selectedBooking.paymentStatus !== 'paid' && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setPaymentMode('cash');
                        setPaymentReference('');
                        setPaymentNotes('');
                        setPaymentProofFile(null);
                        setPaymentProofPreview(null);
                        const remaining = Math.max(0, (selectedBooking.totalCost || 0) - (selectedBooking.totalPaid || 0));
                        // Leave empty; owner enters the actual amount they're recording
                        setPaymentAmount('');
                        setTripConfirmAction({
                          type: 'mark-paid',
                          bookingId: selectedBooking._id,
                          vehicleName: selectedBooking.carId ? formatVehicleName(selectedBooking.carId, { showYear: false }) : 'this booking',
                          amount: selectedBooking.totalCost || 0,
                          totalPaid: selectedBooking.totalPaid || 0,
                          remaining,
                          currentStatus: selectedBooking.status
                        });
                        setTripConfirmModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm font-medium rounded-lg shadow-sm transition-all"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      + Record Payment
                    </button>
                  )}
                  {selectedBooking.totalPaid > 0 && selectedBooking.totalPaid >= selectedBooking.totalCost && (
                    <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Fully Paid
                    </span>
                  )}
                  {/* Generate Invoice */}
                  {selectedBooking.status === 'completed' && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setInvoiceBooking(selectedBooking);
                      }}
                      className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white text-sm font-medium rounded-lg shadow-sm transition-all"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Generate Invoice
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Trip Actions in detail modal (rentals only) */}
            {canTakeAction && selectedBooking.status === 'confirmed' && selectedBooking.rentalType !== 'sale' && (
              <div className="pt-4 border-t">
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDetailsModal(false); handleStartTrip(selectedBooking._id); }}
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Start Trip
                  </button>
                  <button
                    onClick={() => { setShowDetailsModal(false); handleRevertStatus(selectedBooking._id, 'confirmed'); }}
                    className="inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                  >
                    <RotateCcw className="h-5 w-5 mr-2" />
                    Revert to Pending
                  </button>
                </div>
              </div>
            )}

            {/* Sale Action in detail modal — mark vehicle as sold */}
            {canTakeAction && selectedBooking.status === 'confirmed' && selectedBooking.rentalType === 'sale' && (
              <div className="pt-4 border-t">
                <button
                  onClick={() => { setShowDetailsModal(false); handleMarkSold(selectedBooking._id); }}
                  className="w-full inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Mark as Sold
                </button>
              </div>
            )}

            {/* Sold sale — revert back to confirmed */}
            {canTakeAction && selectedBooking.status === 'completed' && selectedBooking.rentalType === 'sale' && (
              <div className="pt-4 border-t">
                <button
                  onClick={() => { setShowDetailsModal(false); handleRevertSale(selectedBooking._id); }}
                  className="w-full inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                >
                  <RotateCcw className="h-5 w-5 mr-2" />
                  Revert (Not Sold)
                </button>
              </div>
            )}

            {canTakeAction && (selectedBooking.status === 'active' || selectedBooking.status === 'in-service') && (
              <div className="pt-4 border-t">
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDetailsModal(false); handleCompleteTrip(selectedBooking._id); }}
                    className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold rounded-xl shadow-lg transition-all"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Complete Trip
                  </button>
                </div>
              </div>
            )}

            {/* Close Button for non-pending */}
            {selectedBooking.status !== 'pending' && (
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reject Booking Modal */}
      <Modal
        isOpen={rejectModal}
        onClose={() => {
          setRejectModal(false);
          setBookingToReject(null);
          setRejectReason('');
        }}
        title=""
        size="md"
      >
        {bookingToReject && (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-rose-600 -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
              <div className="flex items-center text-white">
                <XCircle className="h-6 w-6 mr-2" />
                <h3 className="text-lg font-semibold">Reject Booking</h3>
              </div>
              <p className="text-red-100 text-sm mt-1">This will notify the customer about the rejection</p>
            </div>

            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-3 border">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900">
                    {formatName(bookingToReject.customerName || bookingToReject.customerId?.name) || 'Customer'}
                  </p>
                  <p className="text-sm text-blue-600">
                    {bookingToReject.customerPhone || bookingToReject.customerId?.phone}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Booking #{bookingToReject._id.slice(-6)}</p>
                  <p className="font-semibold text-gray-900">
                    {formatVehicleName(bookingToReject.carId, { showYear: false })}
                  </p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                <span className="text-gray-500">{formatDateTime(bookingToReject.startDate)}</span>
                <span className="font-medium text-gray-900">{formatCurrency(bookingToReject.totalCost || 0)}</span>
              </div>
            </div>

            {/* Quick Rejection Reasons */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Select a reason:</p>
              <div className="grid grid-cols-1 gap-2">
                {[
                  'Vehicle not available for selected dates',
                  'Vehicle is under maintenance',
                  'Incomplete customer information',
                  'Payment verification failed',
                  'Customer request cancelled'
                ].map((reason) => (
                  <button
                    key={reason}
                    onClick={() => setRejectReason(reason)}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${
                      rejectReason === reason
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Reason */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Or write a custom reason:</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason that will be shown to the customer..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start">
                <Clock className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Customer will be notified</p>
                  <p className="text-xs text-amber-700 mt-1">
                    The customer will see this rejection reason when they check their booking status.
                    Make sure the reason is clear and helpful.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setRejectModal(false);
                  setBookingToReject(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectBooking}
                disabled={!rejectReason.trim()}
                className={`flex-1 inline-flex items-center justify-center px-4 py-2.5 font-medium rounded-lg transition-all ${
                  rejectReason.trim()
                    ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Confirm Rejection
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Trip Confirmation Modal (Start Trip / Complete Trip) */}
      {tripConfirmModal && tripConfirmAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className={`px-6 py-6 text-center ${
              tripConfirmAction.type === 'approve'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                : tripConfirmAction.type === 'start'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                : (tripConfirmAction.type === 'revert-payment' || tripConfirmAction.type === 'revert-status' || tripConfirmAction.type === 'revert-sale')
                ? 'bg-gradient-to-r from-amber-500 to-orange-600'
                : tripConfirmAction.type === 'mark-paid'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                : 'bg-gradient-to-r from-emerald-500 to-green-600'
            }`}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 bg-white/20">
                {tripConfirmAction.type === 'approve' ? (
                  <CheckCircle className="h-8 w-8 text-white" />
                ) : tripConfirmAction.type === 'start' ? (
                  <Play className="h-8 w-8 text-white" />
                ) : (tripConfirmAction.type === 'revert-payment' || tripConfirmAction.type === 'revert-status' || tripConfirmAction.type === 'revert-sale') ? (
                  <Undo2 className="h-8 w-8 text-white" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white">
                {tripConfirmAction.type === 'approve' ? 'Approve Booking?'
                  : tripConfirmAction.type === 'start' ? 'Start Trip?'
                  : tripConfirmAction.type === 'revert-payment' ? 'Revert Payment?'
                  : tripConfirmAction.type === 'revert-status' ? 'Revert Status?'
                  : tripConfirmAction.type === 'mark-paid' ? 'Record Payment'
                  : tripConfirmAction.type === 'mark-sold' ? 'Mark as Sold?'
                  : tripConfirmAction.type === 'revert-sale' ? 'Revert (Not Sold)?'
                  : 'Complete Trip?'}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-2 text-center">
                {tripConfirmAction.type === 'approve'
                  ? `Approve booking for ${tripConfirmAction.vehicleName}?`
                  : tripConfirmAction.type === 'start'
                  ? `Start the trip for ${tripConfirmAction.vehicleName}?`
                  : tripConfirmAction.type === 'revert-payment'
                  ? `Revert payment status to pending for ${tripConfirmAction.vehicleName}?`
                  : tripConfirmAction.type === 'revert-status'
                  ? `Revert ${tripConfirmAction.vehicleName} from "${tripConfirmAction.currentStatus}" back to "${tripConfirmAction.revertTo}"?`
                  : tripConfirmAction.type === 'mark-paid'
                  ? `Record payment for ${tripConfirmAction.vehicleName}`
                  : tripConfirmAction.type === 'mark-sold'
                  ? `Mark ${tripConfirmAction.vehicleName} as sold?`
                  : tripConfirmAction.type === 'revert-sale'
                  ? `Revert ${tripConfirmAction.vehicleName} back to confirmed (not sold)?`
                  : `Complete trip for ${tripConfirmAction.vehicleName}?`
                }
              </p>

              {/* Mark-sold: payment summary + warning when not fully paid */}
              {tripConfirmAction.type === 'mark-sold' && tripConfirmAction.amount > 0 && (
                <div className={`mt-3 rounded-lg p-3 text-left text-sm ${tripConfirmAction.notFullyPaid ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-500">Sale Price</span>
                    <span className="font-bold text-gray-900">{formatCurrency(tripConfirmAction.amount)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-500">Paid</span>
                    <span className={`font-semibold ${tripConfirmAction.notFullyPaid ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(tripConfirmAction.totalPaid || 0)}</span>
                  </div>
                  {tripConfirmAction.notFullyPaid && (
                    <>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-500">Balance Due</span>
                        <span className="font-bold text-red-600">{formatCurrency(tripConfirmAction.remaining || 0)}</span>
                      </div>
                      <p className="text-xs text-amber-700 mt-2">
                        ⚠️ This booking isn't fully paid. You can still mark it sold, or click Cancel and record the remaining payment first.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Approve booking details */}
              {tripConfirmAction.type === 'approve' && (
                <div className="mt-3 bg-green-50 rounded-lg p-3 text-left">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-500">Customer</span>
                    <span className="font-medium text-gray-900">{tripConfirmAction.customerName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount</span>
                    <span className="font-bold text-gray-900">₹{tripConfirmAction.amount?.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-green-600 mt-2">Customer will be notified about the approval.</p>
                </div>
              )}

              {/* Payment fields — for Complete Trip AND Mark as Paid */}
              {tripConfirmAction.type === 'complete' && (
                <div className="mt-4 space-y-3 text-left">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Trip Pricing</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setCompleteRentalType('daily')}
                      className={`py-2 rounded-lg text-sm font-medium border ${['daily','rental'].includes(completeRentalType) ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      Per Day (₹{tripConfirmAction.car?.pricePerDay || 0})
                    </button>
                    <button type="button" onClick={() => setCompleteRentalType('per-km')}
                      className={`py-2 rounded-lg text-sm font-medium border ${['per-km','km-based'].includes(completeRentalType) ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                      Per Km (₹{tripConfirmAction.car?.pricePerKm || 0}/km)
                    </button>
                  </div>
                  {['per-km','km-based','outstation'].includes(completeRentalType) && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Km Driven (odometer total)</label>
                      <input type="number" value={completeKm} onChange={(e) => setCompleteKm(e.target.value)}
                        placeholder="Enter total km driven"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  )}
                  <div className="flex justify-between items-center bg-blue-50 rounded-lg p-2.5 text-sm">
                    <span className="text-gray-600">Calculated Total</span>
                    <span className="font-bold text-blue-700">₹{computeCompleteCost().toLocaleString()}</span>
                  </div>
                  <button type="button" onClick={() => setPaymentAmount(String(computeCompleteCost()))}
                    className="text-[11px] text-blue-600 hover:underline">Use calculated total as amount</button>
                </div>
              )}

              {(tripConfirmAction.type === 'complete' || tripConfirmAction.type === 'mark-paid') && (
                <div className="mt-4 space-y-3 text-left">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment Details</p>

                  {/* Amount Summary */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Amount</span>
                      <span className="font-bold">₹{(tripConfirmAction.type === 'complete' ? computeCompleteCost() : tripConfirmAction.amount)?.toLocaleString()}</span>
                    </div>
                    {(tripConfirmAction.totalPaid || 0) > 0 && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-green-600">Already Paid</span>
                        <span className="font-bold text-green-600">₹{tripConfirmAction.totalPaid?.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mt-1 border-t pt-1">
                      <span className="text-gray-700 font-semibold">Remaining</span>
                      <span className="font-bold text-blue-600">₹{tripConfirmAction.remaining?.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Payment Amount Input */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">Amount Receiving Now</label>
                      {(tripConfirmAction.remaining || 0) > 0 && (
                        <button type="button" onClick={() => setPaymentAmount(String(tripConfirmAction.remaining))} className="text-[11px] text-blue-600 hover:underline">
                          Pay remaining (₹{tripConfirmAction.remaining?.toLocaleString()})
                        </button>
                      )}
                    </div>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="Enter amount (e.g. advance)"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  {/* Payment Mode */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Payment Mode</label>
                    <select
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="card">Card</option>
                      <option value="online">Online</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Reference */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Reference / UPI ID <span className="text-gray-400">(optional)</span></label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      placeholder="e.g. UPI txn ID, cheque no."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  {/* Payment Proof Photo */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Payment Proof <span className="text-gray-400">(optional)</span></label>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setPaymentProofFile(file);
                              setPaymentProofPreview(URL.createObjectURL(file));
                            }
                          }}
                        />
                        📷 {paymentProofFile ? 'Change Photo' : 'Upload Photo'}
                      </label>
                      {paymentProofPreview && (
                        <div className="relative">
                          <img src={paymentProofPreview} alt="proof" className="w-12 h-12 rounded-lg object-cover border" />
                          <button
                            type="button"
                            onClick={() => { setPaymentProofFile(null); setPaymentProofPreview(null); }}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Notes <span className="text-gray-400">(optional)</span></label>
                    <input
                      type="text"
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="e.g. Paid via Google Pay"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
              )}

              {tripConfirmAction.type === 'start' && (
                <p className="text-sm text-gray-400 text-center mt-2">The trip will be marked as active.</p>
              )}
              {tripConfirmAction.type === 'revert-payment' && (
                <p className="text-sm text-gray-400 text-center mt-2">Payment will be changed back to pending. You can mark it as paid again later.</p>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => { setTripConfirmModal(false); setTripConfirmAction(null); }}
                  className="flex-1 py-3 rounded-xl font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmTripAction}
                  className={`flex-1 py-3 rounded-xl font-semibold text-white transition-all ${
                    (tripConfirmAction.type === 'approve' || tripConfirmAction.type === 'start')
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                      : (tripConfirmAction.type === 'revert-payment' || tripConfirmAction.type === 'revert-status' || tripConfirmAction.type === 'revert-sale')
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
                      : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700'
                  }`}
                >
                  {tripConfirmAction.type === 'approve' ? 'Yes, Approve Booking'
                    : tripConfirmAction.type === 'start' ? 'Yes, Start Trip'
                    : tripConfirmAction.type === 'revert-payment' ? 'Yes, Revert to Pending'
                    : tripConfirmAction.type === 'revert-status' ? `Yes, Revert to ${tripConfirmAction.revertTo}`
                    : tripConfirmAction.type === 'mark-paid' ? '✓ Record Payment'
                    : tripConfirmAction.type === 'mark-sold' ? '✓ Yes, Mark Sold'
                    : tripConfirmAction.type === 'revert-sale' ? 'Yes, Revert'
                    : '✓ Complete & Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Invoice — uses the SAME JourneyInvoice design (booking mapped to journey shape) */}
      {invoiceBooking && (
        <JourneyInvoice
          journey={bookingToJourney(invoiceBooking)}
          travelsInfo={travelsInfo}
          onClose={() => setInvoiceBooking(null)}
        />
      )}

      {/* Success/Status Modal */}
      {successModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl animate-bounce-in">
            {/* Header */}
            <div className={`px-6 py-6 text-center ${
              successMessage.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
              successMessage.type === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-600' :
              successMessage.type === 'error' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
              'bg-gradient-to-r from-blue-500 to-indigo-600'
            }`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                successMessage.type === 'success' ? 'bg-white/20' :
                successMessage.type === 'warning' ? 'bg-white/20' :
                successMessage.type === 'error' ? 'bg-white/20' :
                'bg-white/20'
              }`}>
                {successMessage.type === 'success' ? (
                  <CheckCircle className="h-8 w-8 text-white" />
                ) : successMessage.type === 'warning' ? (
                  <Clock className="h-8 w-8 text-white" />
                ) : successMessage.type === 'error' ? (
                  <XCircle className="h-8 w-8 text-white" />
                ) : (
                  <CheckCircle className="h-8 w-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white">{successMessage.title}</h3>
            </div>

            {/* Body */}
            <div className="p-6 text-center">
              <p className="text-gray-600 mb-6">{successMessage.message}</p>
              <button
                onClick={() => setSuccessModal(false)}
                className={`w-full py-3 rounded-xl font-semibold transition-all ${
                  successMessage.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white' :
                  successMessage.type === 'warning' ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white' :
                  successMessage.type === 'error' ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white' :
                  'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingList;
